import React, { useEffect, useState } from 'react';
import { GradientText, ModifierModalSkeleton, Spacer } from 'src/common';
import Chart from 'src/components/charts/Chart';
import ChartBlank from 'src/components/charts/ChartBlank';
import { LogSwitch } from 'src/components/charts/comps';
import { CordData } from 'src/components/charts/util';
import { ViewContainer } from '../ViewContainer';
import useBreakpoints from 'src/hooks/useBreakpoints';
import useVersion from 'src/hooks/useVersion';
import { LegendComposed, TableForm } from './comps';
import { useStateSelector } from 'src/redux';
import { isQueryLoading } from 'src/graphql';
import { useScenarioQuery, useScenarioRmpQuery } from 'src/graphql/_generated';

interface ScenarioViewProps {
  scenarioId: string;
}

export const ScenarioView: React.FC<ScenarioViewProps> = ({ scenarioId }) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const [isLogarithmic, setIsLogarithmic] = useState(true);
  const [completeChart, setCompleteChart] = useState<CordData[]>([]);
  const [liveChart, setLiveChart] = useState<CordData[]>([]);
  const { version, VHeader } = useVersion();
  const isMd = useBreakpoints('md');

  const {
    data: scenarioData,
    loading: scenarioLoading,
    networkStatus: scenarioNetworkStatus,
  } = useScenarioQuery({
    variables: {
      id: scenarioId,
      versionId: version,
    },
  });

  const isLoadingScenario = isQueryLoading({
    loading: scenarioLoading,
    data: scenarioData,
    networkStatus: scenarioNetworkStatus,
  });

  const {
    data: scenarioRmpData,
    loading: isRmpLoading,
    networkStatus: rmpNetworkStatus,
  } = useScenarioRmpQuery({
    variables: {
      id: scenarioId,
      versionId: version,
    },
    fetchPolicy: 'no-cache',
  });

  const isLoadingRmp = isQueryLoading({
    loading: isRmpLoading,
    data: scenarioRmpData,
    networkStatus: rmpNetworkStatus,
  });

  const scenario = scenarioData?.scenario;

  const scenarioLive = scenario?.liveRisk;
  const scenarioBase = scenario?.baseRisk;
  const scenarioProjected = scenario?.projectedRisk;

  useEffect(() => {
    if (scenarioProjected?.chart && completeChart.length === 0) {
      setCompleteChart(scenarioProjected.chart);
    }
  }, [scenarioProjected, completeChart.length]);

  const riskManagementPlans = scenarioRmpData?.scenario?.riskManagementPlans || [];

  const isValidScenario = scenarioLive?.expectedLoss != null;

  const showAllProjected = !!scenarioRmpData?.scenario?.riskManagementPlans
    ?.filter((rmp) => rmp.control && rmp.control.status !== 'Abandoned' && rmp.control.status !== 'Retired')
    ?.flatMap((rmp) => rmp.modifiers)
    ?.filter((modifier) => modifier.type !== 'NOTE_TYPE')?.length;

  const isLoading = isLoadingScenario || isLoadingRmp;

  return (
    <ViewContainer>
      <div className={!isLoading && scenario ? '' : 'closed'}>
        <div className="sw-top">
          <GradientText>LOSS EXCEEDANCE CURVE</GradientText>
          <div className="sw-top__end">
            {!isMd && <LegendComposed />}
            <LogSwitch skey="log:pr_scenario" isLogarithmic={isLogarithmic} setIsLogarithmic={setIsLogarithmic} />
          </div>
        </div>
        {isMd && (
          <>
            <Spacer $px={10} />
            <LegendComposed />
          </>
        )}
        {VHeader && (
          <>
            <Spacer $px={10} />
            {VHeader}
          </>
        )}
        <Spacer $px={20} />
        {isValidScenario && !isLoading ? (
          <Chart
            isLogarithmic={isLogarithmic}
            // @ts-ignore
            input={[
              showAllProjected && {
                data: scenarioBase?.chart || [],
                upper: scenarioBase?.upper || 1,
                lower: scenarioBase?.lower || 1,
                name: 'Unmodified Risk',
                color: colors.prussianBlue,
                fillColor: 'rgba(2, 3, 51, 0.07)',
                legendId: 'leg-base-pv',
              },
              {
                data: liveChart.length > 0 ? liveChart : scenarioLive?.chart || [],
                upper: scenarioLive?.upper || 1,
                lower: scenarioLive?.lower || 1,
                name: 'Live Risk',
                color: colors.gp2,
                fillColor: 'rgba(230, 46, 97, 0.07)',
                legendId: 'leg-s1-pv',
              },
              showAllProjected && {
                data: completeChart.length > 0 ? completeChart : scenarioProjected?.chart || [],
                upper: scenarioProjected?.upper || scenarioLive?.upper || 1,
                lower: scenarioProjected?.lower || scenarioLive?.lower || 1,
                name: 'Projected Risk',
                color: colors.brightBlue,
                fillColor: 'rgba(100, 149, 237, 0.07)',
                legendId: 'leg-s2-pv',
              },
            ].filter(Boolean)}
          />
        ) : (
          <ChartBlank />
        )}
        <Spacer $px={40} />
        {scenario && riskManagementPlans.length > 0 && (
          <TableForm
            scenario={scenario}
            setLiveChart={setLiveChart}
            setCompleteChart={setCompleteChart}
            riskManagementPlans={riskManagementPlans}
            isInModal={true}
          />
        )}
      </div>
      {isLoading && <ModifierModalSkeleton />}
    </ViewContainer>
  );
};
